import type { useWebSocketStore } from '../stores/useWebSocketStore'
import type { useChatStore } from '../stores/useChatStore'
import type { useAuthStore } from '@/stores/useAuthStore'
import { useMessageStore } from '../stores/useMessageStore'
import type {
  Template,
  RoutingDestination,
  ContactSelection,
  SendTemplateDestination,
  DestinationAction,
  TemplateSendComponent,
  TemplateSendObject,
  MessageContent,
} from '../types/interfaces'
import { useFileFormatter } from '../composables/useFileFormatter'
import { isQueuedChat } from '../stores/useChatStore'

const { fileToBase64 } = useFileFormatter()

/**
 * El envio fallo ANTES de dejar nada en el hilo: no hay mensaje que el agente
 * pueda ver, copiar ni reintentar, asi que quien llama tiene que devolverle el
 * texto. Se distingue del fallo posterior, donde el mensaje si queda visible
 * marcado como fallido y devolver el texto lo duplicaria.
 */
export class MessageNotQueuedError extends Error {}

export const createChatOperations = (
  webSocketStore: ReturnType<typeof useWebSocketStore>,
  chatStore: ReturnType<typeof useChatStore>,
  authStore: ReturnType<typeof useAuthStore>,
) => {
  /**
   * Avisa al backend hasta donde leyo el agente. Se llama al abrir el chat y
   * cada vez que entra un mensaje con ese chat ya abierto: ahi el contador local
   * no cambia (no suma si el chat esta seleccionado), pero la marca del servidor
   * si tiene que avanzar o esos mensajes reapareceran sin leer tras recargar.
   */
  const markChatRead = (chatId: string) => {
    if (!webSocketStore.isConnected) return

    const userId = chatStore.currentUserId
    if (!userId) return

    // Un chat de la cola no es de nadie: no hay fila ENTER donde apuntar la
    // lectura, asi que el backend ignoraria el aviso. Se evita el frame.
    const chat = chatStore.chats.find((c) => c.chat_id === chatId)
    if (!chat || isQueuedChat(chat)) return

    webSocketStore.getService().markChatRead({
      type: 'READ_CHAT',
      data: {
        chat_id: chatId,
        user_id: userId,
      },
    })
  }

  const selectChat = async (chatId: string) => {
    chatStore.selectChat(chatId)
    markChatRead(chatId)
  }

  const takeChat = async (chatId: string) => {
    await selectChat(chatId)
    const service = webSocketStore.getService()
    await service.enterChat({
      type: 'ENTER_TO_CHAT',
      data: {
        chat_id: chatId,
        campaign_id: chatStore.selectedChat?.campaign_id || '',
        user_id: chatStore.currentUserId || '',
        user_name: authStore.userAlias || authStore.userName || 'Usuario',
      },
    })
  }

  const exitCurrentChat = () => {
    if (!chatStore.selectedChatId) return

    chatStore.selectChat(null)
  }

  const endCurrentChat = () => {
    if (!chatStore.selectedChatId) return

    const service = webSocketStore.getService()
    service.endChat({
      type: 'END_TO_CHAT',
      data: {
        chat_id: chatStore.selectedChatId,
        campaign_id: chatStore.selectedChat?.campaign_id || '',
        user_id: chatStore.currentUserId || '',
        user_name: authStore.userAlias || authStore.userName || 'Usuario',
      },
    })
  }

  const sendTextMessage = async (
    chatId: string,
    text: string,
    replyToId?: string,
    aiTranslate?: MessageContent['ai_translate'],
  ) => {
    // Validar conexión WebSocket
    if (!webSocketStore.isConnected) {
      throw new MessageNotQueuedError(
        'No hay conexión con el servidor. Por favor espera a que se restablezca la conexión.',
      )
    }

    const chat = chatStore.chats.find((c) => c.chat_id === chatId)
    if (!chat) {
      throw new MessageNotQueuedError(`No se encontró el chat ${chatId}`)
    }

    // Sin teléfono no se bloquea: el cliente que adoptó username se identifica
    // por BSUID y el backend resuelve el destinatario desde el chat.
    const messageStore = useMessageStore()

    const tempMessage = messageStore.createTextMessage(
      chatId,
      text,
      chatStore.currentUserId || undefined,
      replyToId,
      aiTranslate,
    )
    messageStore.addTemporaryMessage(tempMessage)

    const service = webSocketStore.getService()
    const isSignatureEnabled = chatStore.isChatSignatureEnabled(chatId)

    try {
      const queued = service.sendMessage({
        type: 'SEND_MESSAGE',
        data: {
          chat_id: chatId,
          message_id: tempMessage.id,
          destination: chat.contact_phone || null,
          user_id: chatStore.currentUserId || '',
          is_sign: isSignatureEnabled,
          message: tempMessage.content,
        },
      })

      // El socket puede estar ya cerrado aunque `isConnected` todavia diga que
      // no: sin esto el mensaje se quedaba en el hilo con el reloj de PENDING
      // y nadie se enteraba de que no habia salido.
      if (!queued) throw new Error(`El WebSocket no acepto el mensaje ${tempMessage.id}`)
    } catch (error) {
      messageStore.markMessageAsFailed(tempMessage.id)
      throw error
    }
  }

  const sendFileMessage = async (
    chatId: string,
    file: File,
    caption?: string,
    isVoiceNote?: boolean,
  ) => {
    if (!webSocketStore.isConnected) {
      throw new MessageNotQueuedError(
        'No hay conexión con el servidor. Por favor espera a que se restablezca la conexión.',
      )
    }

    const chat = chatStore.chats.find((c) => c.chat_id === chatId)
    if (!chat) {
      throw new MessageNotQueuedError(`No se encontró el chat ${chatId}`)
    }

    const messageStore = useMessageStore()

    const tempMessage = messageStore.createFileMessage(
      chatId,
      file,
      chatStore.currentUserId || undefined,
      caption,
    )
    messageStore.addTemporaryMessage(tempMessage)

    const fileBase64 = await fileToBase64(file)
    const fileExtension = file.name.split('.').pop() || ''

    const service = webSocketStore.getService()
    const isSignatureEnabled = chatStore.isChatSignatureEnabled(chatId)

    try {
      const queued = service.sendFile({
        type: 'SEND_FILE',
        data: {
          message_id: tempMessage.id,
          destination: chat.contact_phone || null,
          user_id: chatStore.currentUserId || '',
          chat_id: chatId,
          is_sign: isSignatureEnabled,
          message: {
            ...tempMessage.content,
            file_base64: fileBase64,
            file_content_type: file.type,
            file_extension: fileExtension,
            is_voice_note: isVoiceNote || undefined,
          },
        },
      })

      if (!queued) throw new Error(`El WebSocket no acepto el archivo ${tempMessage.id}`)
    } catch (error) {
      messageStore.markMessageAsFailed(tempMessage.id)
      throw error
    }
  }

  const sendTemplate = async (
    destinations: ContactSelection[],
    template: Template,
    destination: RoutingDestination,
    incomingRouteId: string | null,
    account_id: string,
    templateComponents?: TemplateSendComponent[] | null,
    campaignId?: string,
    chatId?: string,
    isBulk = false,
    confirmTakeover = false,
  ) => {
    const service = webSocketStore.getService()

    const formattedDestinations: SendTemplateDestination[] = destinations.map((d) => ({
      id: d.id || d.campaign_id || '',
      phone: d.phone,
      bsuid: d.bsuid || null,
      name: d.name || '',
    }))

    let destinationType: 'CAMPANA' | 'BOT' | 'AGENT' | 'FINISH'
    let resourceId: string | null = null

    if (destination.type === 'campaign') {
      destinationType = 'CAMPANA'
      resourceId = destination.campaign_id || null
    } else if (destination.type === 'bot') {
      destinationType = 'BOT'
      resourceId = destination.bot_id || null
    } else if (destination.type === 'user') {
      destinationType = 'AGENT'
      resourceId = destination.user_id || null
    } else {
      destinationType = 'FINISH'
      resourceId = null
    }

    const destinationAction: DestinationAction = {
      resource_id: resourceId,
      destination_type: destinationType,
      message: null,
    }

    const templateBase: TemplateSendObject = template.content.template ?? {
      name: template.template_name || template.content.name,
      namespace: null,
      language: { code: template.content.language },
      components: [],
    }

    const builtTemplate: TemplateSendObject = templateComponents
      ? { ...templateBase, components: templateComponents }
      : templateBase

    return service.sendTemplate({
      type: 'SEND_TEMPLATE',
      data: {
        template_id: template.id,
        incoming_route_id: incomingRouteId,
        account_id: account_id,
        is_bulk: isBulk,
        destinations: formattedDestinations,
        destination_action: destinationAction,
        campaign_id: campaignId,
        user_id: chatStore.currentUserId || '',
        ...(chatId && { chat_id: chatId }),
        template: builtTemplate,
        confirm_takeover: confirmTakeover,
      },
    })
  }

  const transferChatToUser = async (
    chatId: string,
    fromCampaignId: string,
    fromCampaignName: string,
    toUserId: string,
    toUserName: string,
    toCampaignId: string,
    toCampaignName: string,
    contactName: string,
    contactPhone: string,
    transferMessage: string,
  ) => {
    const service = webSocketStore.getService()

    return service.transferToUser({
      type: 'TRANSFER_TO_USER',
      data: {
        chat_id: chatId,
        from_user_id: chatStore.currentUserId || '',
        from_user_name: authStore.userAlias || authStore.userName || 'Usuario',
        from_campaign_id: fromCampaignId,
        from_campaign_name: fromCampaignName,
        to_user_id: toUserId,
        to_user_name: toUserName,
        to_campaign_id: toCampaignId,
        to_campaign_name: toCampaignName,
        contact_name: contactName,
        contact_phone: contactPhone,
        transfer_message: transferMessage,
      },
    })
  }

  const transferChatToCampaign = async (
    chatId: string,
    campaignId: string,
    campaignName: string,
  ) => {
    const service = webSocketStore.getService()

    return service.transferToCampaign({
      type: 'TRANSFER_TO_CAMPAIGN',
      data: {
        chat_id: chatId,
        user_id: chatStore.currentUserId || '',
        user_name: authStore.userAlias || authStore.userName || 'Usuario',
        campaign_id: campaignId,
        campaign_name: campaignName,
      },
    })
  }

  const transferChatToBot = async (
    chatId: string,
    botId: string,
    botName: string,
    campaignId: string,
    incomingRouteId: string,
    phone: string,
  ) => {
    const service = webSocketStore.getService()

    return service.transferToBot({
      type: 'TRANSFER_TO_BOT',
      data: {
        chat_id: chatId,
        user_id: chatStore.currentUserId || '',
        user_name: authStore.userAlias || authStore.userName || 'Usuario',
        bot_id: botId,
        bot_name: botName,
        campaign_id: campaignId,
        incoming_route_id: incomingRouteId,
        phone: phone,
      },
    })
  }

  const confirmTransfer = async (
    chatId: string,
    isAccepted: boolean,
    fromUserId: string,
    fromUserName: string,
    toUserId: string,
    toUserName: string,
    toCampaignId: string,
  ) => {
    const service = webSocketStore.getService()

    return service.confirmTransfer({
      type: 'CONFIRM_TRANSFER',
      data: {
        chat_id: chatId,
        is_accepted: isAccepted,
        from_user_id: fromUserId,
        from_user_name: fromUserName,
        to_user_id: toUserId,
        to_user_name: toUserName,
        to_campaign_id: toCampaignId,
      },
    })
  }

  return {
    selectChat,
    takeChat,
    markChatRead,
    exitCurrentChat,
    endCurrentChat,
    sendTextMessage,
    sendFileMessage,
    sendTemplate,
    transferChatToUser,
    transferChatToCampaign,
    transferChatToBot,
    confirmTransfer,
  }
}
